import { cn } from "@theprelude/core";
import { InputHTMLAttributes, forwardRef } from "react";
import { RequiredCaret } from "../required";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  before?: React.ReactNode;
  after?: React.ReactNode;
  wrapperClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, before, after, wrapperClassName, ...props },
  ref,
) {
  return (
    <div className={cn("relative flex h-full", wrapperClassName)}>
      {before && (
        <div className="absolute bottom-0 left-0 top-0 flex items-center pl-2">
          {before}
        </div>
      )}
      <input
        ref={ref}
        className={cn(
          "bg-surface-background border-stroke-input focus:border-primary-foreground h-9 w-full rounded border px-3 py-2 text-sm focus:outline-none",
          before && "pl-8",
          after && "pr-8",
          className,
        )}
        autoComplete="off"
        {...props}
      />
      {after && (
        <div className="absolute bottom-0 right-0 top-0 flex items-center pr-2">
          {after}
        </div>
      )}
    </div>
  );
});

export const InputField: React.FC<{
  children: React.ReactNode;
  size?: "sm" | "md";
  id?: string;
  label?: string;
  error?: string;
  required?: boolean;
  className?: string;
}> = ({
  children,
  size = "md",
  id,
  label,
  error,
  required = false,
  className,
}) => (
  <div
    className={cn(
      "flex flex-col",
      size === "md" ? "gap-2" : "gap-1",
      className,
    )}
  >
    {label && (
      <label
        className={
          size === "md"
            ? "text-base"
            : "text-secondary-foreground text-xs font-medium"
        }
        htmlFor={id}
      >
        {label} {required && <RequiredCaret />}
      </label>
    )}
    {children}
    {error && <InputFieldError error={error} />}
  </div>
);

export const InputFieldError: React.FC<{
  error: string;
  className?: string;
}> = ({ error, className }) => (
  <div className={cn("text-status-red text-xs", className)}>{error}</div>
);
